// ── Phone-native shell (mobile ≤900px) ──────────────────────────────────────
//
// The desktop dashboard is a left section-tree + content well. On a phone that
// model doesn't hold (feedback 2026-07-28: "they can't follow the same thing"),
// so below the mobile breakpoint the shell is re-architected — NOT shrunk:
//
//   · a bottom tab bar (Home · Sections · Ask · More) — the primary nav,
//   · a context top bar (workspace + scope) that is HIDDEN on Home and tucks
//     away on scroll-down (reappears on scroll-up), like the deployed site,
//   · a single bottom-sheet navigator with two modes — the current workspace's
//     section tree, and a six-workspace picker — dismissed by dragging down or
//     tapping the scrim (no close button; feedback 2026-07-28),
//   · the SAME section components as desktop, rendered as `children`.
//
// All navigation STATE + actions live in the parent Dashboard; this component is
// pure chrome. The nav model was validated in a throwaway prototype first (see
// memory `mobile-shell-nav-model`). Desktop is untouched — Dashboard branches on
// `useBelow("mobile")` and only reaches here on a phone-width viewport.

import {
  Check,
  ChevronDown,
  ChevronLeft,
  Home as HomeIcon,
  Layers,
  LogOut,
  MoreHorizontal,
  Sparkles,
} from "lucide-react";
import { type CSSProperties, type ReactNode, Suspense, useEffect, useRef, useState } from "react";
import { canLogout, getEmail, logout } from "../../lib/auth";
import { lazyRetry } from "../../lib/lazy-retry";
import { APP_NAME, COUNTRY, TENANT_PROFILE } from "../../lib/tenant";
import { THEME_SWITCHING_ENABLED, type Theme, ThemeToggle } from "../../lib/theme";
import type { SectionGroup } from "./section-tree";
import { FM, FU, accentInk } from "./tokens";
import { WORKSPACES, type WorkspaceId } from "./workspaces";

// The assistant drawer pulls in the AI SDK + a markdown renderer, so it stays a
// code-split chunk fetched the first time "Ask" is tapped (mirrors AssistantPanel).
const AssistantDrawer = lazyRetry(() => import("./assistant-drawer").then((m) => ({ default: m.AssistantDrawer })));
import type { AssistantContext } from "./assistant-drawer";

type View = "global" | "saudi";
type Sheet = null | "nav" | "more";
type NavMode = "tree" | "ws";

interface Props {
  /** On the landing hub — the top bar is suppressed and Sections opens the
      workspace picker rather than a workspace's tree. */
  isHome: boolean;
  workspace: WorkspaceId;
  workspaceLabel: string;
  /** Workspace accent hue — the context dot + selection tint. */
  accent: string;
  /** Carries the Global/Saudi scope pill (Intelligence, Demand Signals). */
  scoped: boolean;
  view: View;
  /** Section groups for the active workspace/scope (already resolved by parent). */
  groups: SectionGroup[];
  activeId: string;
  onPickItem: (id: string) => void;
  /** Enter a workspace (leaves the hub, lands on its first section). */
  onPickWorkspace: (id: WorkspaceId) => void;
  onSelectView: (v: View) => void;
  /** Return to the landing hub. */
  onHome: () => void;
  theme: Theme;
  setTheme: (t: Theme) => void;
  assistantContext?: AssistantContext;
  /** The active section body (or the hub / segmentation landing) — same
      components as desktop. */
  children: ReactNode;
}

export function MobileShell({
  isHome,
  workspace,
  workspaceLabel,
  accent,
  scoped,
  view,
  groups,
  activeId,
  onPickItem,
  onPickWorkspace,
  onSelectView,
  onHome,
  theme,
  setTheme,
  assistantContext,
  children,
}: Props) {
  const [sheet, setSheet] = useState<Sheet>(null);
  const [navMode, setNavMode] = useState<NavMode>("tree");
  // The assistant is its own overlay (AssistantDrawer), not one of our sheets.
  // Once opened it stays mounted so the conversation survives close/reopen.
  const [askOpen, setAskOpen] = useState(false);
  const [askMounted, setAskMounted] = useState(false);
  const [chromeHidden, setChromeHidden] = useState(false);
  const sheetRef = useRef<HTMLElement>(null);

  // ── Hide the top + bottom bars on scroll-down, reveal on scroll-up ──────────
  useEffect(() => {
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const d = y - last;
      if (y <= 8)
        setChromeHidden(false); // at the top → always shown
      else if (d > 4 && y > 56)
        setChromeHidden(true); // scrolling down
      else if (d < -4) setChromeHidden(false); // scrolling up
      last = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock background scroll while a sheet is open.
  useEffect(() => {
    if (!sheet) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [sheet]);

  // Esc closes the open sheet.
  useEffect(() => {
    if (!sheet) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSheet(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sheet]);

  // ── Drag the sheet down to dismiss (no close button) ────────────────────────
  useEffect(() => {
    const el = sheetRef.current;
    if (!el || !sheet) return;
    let startY = 0;
    let dy = 0;
    let dragging = false;
    const down = (e: PointerEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest("button")) return; // let taps on rows / switcher work
      if (!t.closest(".msh-grab, .msh-sheet-h")) return; // drag only by handle / header
      startY = e.clientY;
      dy = 0;
      dragging = true;
      el.style.transition = "none";
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        // synthetic / unsupported pointer — non-fatal
      }
    };
    const move = (e: PointerEvent) => {
      if (!dragging) return;
      dy = Math.max(0, e.clientY - startY);
      el.style.transform = `translateY(${dy}px)`;
    };
    const end = () => {
      if (!dragging) return;
      dragging = false;
      el.style.transition = "";
      el.style.transform = "";
      if (dy > 90) setSheet(null); // dragged far enough → dismiss
      dy = 0;
    };
    el.addEventListener("pointerdown", down);
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", end);
    el.addEventListener("pointercancel", end);
    return () => {
      el.removeEventListener("pointerdown", down);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", end);
      el.removeEventListener("pointercancel", end);
    };
  }, [sheet]);

  const openNav = () => {
    setNavMode(isHome ? "ws" : "tree");
    setSheet("nav");
  };
  const goHome = () => {
    setSheet(null);
    setAskOpen(false);
    onHome();
  };
  const pickWorkspace = (id: WorkspaceId) => {
    onPickWorkspace(id);
    setNavMode("tree"); // drop into the picked workspace's tree, stay in the sheet
  };
  const pickSection = (id: string) => {
    onPickItem(id);
    setSheet(null);
  };

  return (
    <div
      className="msh"
      data-home={isHome}
      data-chrome-hidden={chromeHidden}
      style={{ "--msh-accent": accent } as CSSProperties}
    >
      {/* ── Context top bar — workspace + scope. Suppressed on Home. ─────────── */}
      {!isHome && (
        <header className="msh-top">
          <div className="msh-top-row">
            <div className="msh-kick">
              <span className="msh-dot" aria-hidden="true" />
              {workspaceLabel}
            </div>
            {scoped && (
              <div className="msh-scope" role="tablist" aria-label="Scope">
                <button
                  type="button"
                  role="tab"
                  aria-selected={view === "global"}
                  data-active={view === "global"}
                  onClick={() => onSelectView("global")}
                >
                  Global
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={view === "saudi"}
                  data-active={view === "saudi"}
                  onClick={() => onSelectView("saudi")}
                >
                  {COUNTRY}
                </button>
              </div>
            )}
          </div>
        </header>
      )}

      {/* ── Content — the same section components as desktop. Reuses .dash-main so
           the section mobile CSS (kicker wrapping, full-bleed bands) still
           applies; .msh-main adds the fixed-bar clearances. ─────────────────── */}
      <main className="dash-main msh-main">{children}</main>

      {/* ── Bottom tab bar — the primary navigation. ────────────────────────── */}
      <nav className="msh-bottom" aria-label="Primary">
        <button type="button" className="msh-tab" data-active={isHome && !sheet && !askOpen} onClick={goHome}>
          <HomeIcon size={19} strokeWidth={1.9} aria-hidden="true" />
          <span>Home</span>
        </button>
        <button type="button" className="msh-tab" data-active={sheet === "nav"} onClick={openNav}>
          <Layers size={19} strokeWidth={1.9} aria-hidden="true" />
          <span>Sections</span>
        </button>
        {TENANT_PROFILE.assistantEnabled && (
          <button
            type="button"
            className="msh-tab"
            data-active={askOpen}
            onClick={() => {
              setAskMounted(true);
              setAskOpen(true);
            }}
          >
            <Sparkles size={19} strokeWidth={1.9} aria-hidden="true" />
            <span>Ask</span>
          </button>
        )}
        <button type="button" className="msh-tab" data-active={sheet === "more"} onClick={() => setSheet("more")}>
          <MoreHorizontal size={19} strokeWidth={1.9} aria-hidden="true" />
          <span>More</span>
        </button>
      </nav>

      {/* ── Scrim + bottom sheet ────────────────────────────────────────────── */}
      {sheet && <button type="button" className="msh-scrim" aria-label="Close" onClick={() => setSheet(null)} />}
      {/* A native <dialog> would own its own backdrop and require imperative
          showModal()/close(), which fights the drag-to-dismiss transform and the
          separate .msh-scrim this sheet animates independently. Kept as an ARIA
          dialog; Esc + scrim-tap dismissal are wired above. */}
      <section
        ref={sheetRef}
        className={`msh-sheet${sheet ? " on" : ""}`}
        // biome-ignore lint/a11y/useSemanticElements: bottom sheet — see above
        role="dialog"
        aria-modal="true"
        aria-hidden={!sheet}
      >
        <div className="msh-grab" aria-hidden="true" />

        {/* Workspace picker */}
        {sheet === "nav" && navMode === "ws" && (
          <>
            <div className="msh-sheet-h">
              {!isHome && (
                <button
                  type="button"
                  className="msh-back"
                  onClick={() => setNavMode("tree")}
                  aria-label="Back to sections"
                >
                  <ChevronLeft size={18} strokeWidth={2} aria-hidden="true" />
                </button>
              )}
              <span className="msh-sheet-t">{isHome ? "Choose a workspace" : "Switch workspace"}</span>
            </div>
            <div className="msh-sheet-body">
              {WORKSPACES.map((w) => (
                <button
                  type="button"
                  key={w.id}
                  className="msh-wrow"
                  data-active={!isHome && w.id === workspace}
                  // --wac paints the row's index as TEXT (plus a tinted chip),
                  // so it needs the darkened twin on light; no-op on dark.
                  style={{ "--wac": accentInk(w.accent, theme === "light") } as CSSProperties}
                  onClick={() => pickWorkspace(w.id)}
                >
                  <span className="msh-wrow-n">{w.n}</span>
                  <span className="msh-wrow-b">
                    <span className="msh-wrow-l">{w.label}</span>
                    <span className="msh-wrow-d">{w.blurb}</span>
                  </span>
                  <Check className="msh-chk" size={17} strokeWidth={2.2} aria-hidden="true" />
                </button>
              ))}
            </div>
          </>
        )}

        {/* Section tree (current workspace) — header doubles as the workspace switcher */}
        {sheet === "nav" && navMode === "tree" && (
          <>
            <div className="msh-sheet-h msh-nav-h">
              <button
                type="button"
                className="msh-wsbtn"
                onClick={() => setNavMode("ws")}
                aria-haspopup="true"
                aria-label={`Workspace: ${workspaceLabel}. Tap to switch workspace.`}
              >
                <span className="msh-wsdot" aria-hidden="true" />
                <span className="msh-wsn">{workspaceLabel}</span>
                <ChevronDown className="msh-wschev" size={18} strokeWidth={2} aria-hidden="true" />
              </button>
            </div>
            <div className="msh-sheet-body">
              {groups.map((g) => (
                <div key={g.id} className="msh-grp">
                  {!g.flat && (
                    <div className="msh-grp-h">
                      <span className="msh-grp-n">{g.n}</span>
                      {g.label}
                    </div>
                  )}
                  {g.items
                    .filter((it) => !it.href)
                    .map((it) => (
                      <button
                        type="button"
                        key={it.id}
                        className="msh-srow"
                        data-active={it.id === activeId}
                        onClick={() => pickSection(it.id)}
                      >
                        <span className="msh-sn">{it.n}</span>
                        <span className="msh-snm">{it.label}</span>
                        <Check className="msh-chk" size={17} strokeWidth={2.2} aria-hidden="true" />
                      </button>
                    ))}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Account / more */}
        {sheet === "more" && (
          <>
            <div className="msh-sheet-h">
              <span className="msh-sheet-t">Account</span>
            </div>
            <div className="msh-sheet-body">
              <div className="msh-acct">
                <span className="msh-acct-n">{getEmail() ?? "Signed in"}</span>
                <span className="msh-acct-e">{APP_NAME}</span>
              </div>
              {THEME_SWITCHING_ENABLED && (
                <div className="msh-more-row">
                  <ThemeToggle theme={theme} setTheme={setTheme} variant="dark-surface" />
                </div>
              )}
              {canLogout() && (
                <button type="button" className="msh-signout" onClick={logout}>
                  <LogOut size={16} strokeWidth={2} aria-hidden="true" />
                  Sign out
                </button>
              )}
            </div>
          </>
        )}
      </section>

      {/* ── Assistant (Ask) — its own overlay, mounted lazily on first open ──── */}
      {TENANT_PROFILE.assistantEnabled && askMounted && (
        <Suspense fallback={null}>
          <AssistantDrawer open={askOpen} context={assistantContext} onClose={() => setAskOpen(false)} />
        </Suspense>
      )}

      <style>{MSH_STYLES}</style>
    </div>
  );
}

const MSH_STYLES = `
  .msh {
    /* Dark navy island — the same palette as the desktop sidebar (.tryside), so
       the chrome reads as one system. This block is the DARK half of the pair;
       the [data-theme="light"] block below re-points the same vars + surfaces,
       so nothing here changes and dark stays byte-identical. */
    --msh-bar: #0b1226;
    --msh-surface: rgba(255, 255, 255, 0.05);
    --msh-surface2: rgba(255, 255, 255, 0.085);
    --msh-line: rgba(255, 255, 255, 0.10);
    --msh-line2: rgba(255, 255, 255, 0.185);
    --msh-hi: #e9eefc;
    --msh-md: #98a3c2;
    --msh-lo: #69739a;
    --msh-navfill: rgba(233, 238, 252, 0.13);
    --msh-navedge: rgba(233, 238, 252, 0.72);

    position: relative;
    min-height: 100vh;
    background:
      radial-gradient(120% 32% at 50% -4%, rgba(130, 186, 255, 0.10), transparent 62%),
      linear-gradient(180deg, #0d1330 0%, #090c1a 55%, #080b16 100%);
    color: var(--msh-hi);
    font-family: ${FU};
  }

  /* ── Light twin (design-system §0.7: paired, never a dark value edited) ─────
     The phone chrome is the same family as the desktop rail, so it inverts the
     way .tryside does: the near-black navy fall becomes the pale blue-white
     fall, the crown glow keeps its blue identity at a lifted alpha (a glow
     reads weaker on light), and the sheet becomes an opaque white island with a
     navy hairline — exactly the .wsw-menu treatment. Inks land on the canonical
     text tokens' light values (#000f47 / #55617f / #767f95, all ≥4.5:1 here).
     Every declaration is an ADD under [data-theme="light"], so dark renders
     byte-identical. */
  [data-theme="light"] .msh {
    --msh-bar: #eef3fc;
    --msh-surface: rgba(0, 15, 71, 0.045);
    --msh-surface2: rgba(0, 15, 71, 0.075);
    --msh-line: rgba(0, 15, 71, 0.12);
    --msh-line2: rgba(0, 15, 71, 0.20);
    --msh-hi: #000f47;
    --msh-md: #55617f;
    --msh-lo: #767f95;
    --msh-navfill: rgba(0, 15, 71, 0.07);
    --msh-navedge: rgba(0, 15, 71, 0.42);

    background:
      radial-gradient(120% 32% at 50% -4%, rgba(120, 175, 255, 0.28), transparent 62%),
      linear-gradient(180deg, #f0f3fa 0%, #e7eef7 55%, #dfe7f3 100%);
  }
  [data-theme="light"] .msh-top { background: linear-gradient(180deg, #eef3fc 0%, #f1f5fc 100%); }
  [data-theme="light"] .msh-bottom { background: linear-gradient(0deg, #eef3fc 0%, #f4f7fc 100%); }
  [data-theme="light"] .msh-sheet {
    background: #ffffff;
    box-shadow: 0 -18px 50px -12px rgba(0, 15, 71, 0.18);
  }
  [data-theme="light"] .msh-scrim { background: rgba(0, 15, 71, 0.28); }

  /* ── Content well ─────────────────────────────────────────────────────────
     Reuses the desktop side padding (clamp) so the sections' full-bleed bands —
     which cancel it with a --bleed-sized negative margin — stay flush and never
     scroll the page sideways. Top clearance = the fixed top bar; 0 on Home. */
  .msh-main {
    margin-left: 0;
    max-width: 100%;
    padding-left: clamp(20px, 4vw, 56px);
    padding-right: clamp(20px, 4vw, 56px);
    padding-top: 50px;
    padding-bottom: 74px;
    /* Backstop: clip any horizontal overflow a section's content would create.
       The chrome (top bar, the .src-bg fixed background) is position:fixed and
       balloons to the document's scroll width the moment ANY in-flow element
       exceeds the viewport — which drags the fixed bars off-screen. Clipping the
       content well here means a stray-wide element degrades to a small edge clip
       instead of breaking the whole shell. Inner swipe strips (scatter/matrix
       ScrollXOnPhone, wide tables) keep their own overflow-x:auto, so they still
       scroll; full-bleed bands sit at the well's edge, so they're not clipped.
       .msh-main only exists on mobile, so desktop is untouched. */
    overflow-x: clip;
  }
  .msh[data-home="true"] .msh-main {
    padding-left: 0;
    padding-right: 0;
    padding-top: 0;
  }

  /* ── Context top bar ──────────────────────────────────────────────────────
     Workspace + scope only — the section's own opening carries its identity, so
     nothing is repeated here. Slides up out of view on scroll-down. */
  .msh-top {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 40;
    background: linear-gradient(180deg, #0d1330 0%, #0a0e20 100%);
    border-bottom: 1px solid var(--msh-line);
    -webkit-backdrop-filter: blur(12px);
    backdrop-filter: blur(12px);
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .msh[data-chrome-hidden="true"] .msh-top { transform: translateY(-100%); }
  .msh-top-row {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 50px;
    padding: 0 16px;
  }
  .msh-kick {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1 1 auto;
    min-width: 0;
    font-family: ${FM};
    text-transform: uppercase;
    letter-spacing: 0.2em;
    font-size: 11px;
    color: var(--msh-md);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .msh-dot {
    width: 8px;
    height: 8px;
    border-radius: 2.5px;
    flex: 0 0 auto;
    background: var(--msh-accent);
    box-shadow: 0 0 9px -1px var(--msh-accent);
  }
  .msh-scope {
    flex: 0 0 auto;
    display: flex;
    height: 34px;
    padding: 3px;
    gap: 2px;
    border-radius: 10px;
    background: var(--msh-surface);
    border: 1px solid var(--msh-line);
  }
  .msh-scope button {
    font-family: ${FU};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
    border: 0;
    background: transparent;
    color: var(--msh-md);
    border-radius: 7px;
    padding: 0 13px;
    cursor: pointer;
  }
  .msh-scope button[data-active="true"] {
    background: var(--msh-navfill);
    color: var(--msh-hi);
    box-shadow: inset 0 0 0 1px var(--msh-navedge);
  }

  /* ── Bottom tab bar ───────────────────────────────────────────────────────*/
  .msh-bottom {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 40;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    background: linear-gradient(0deg, #0b1024 0%, #0c1128 100%);
    border-top: 1px solid var(--msh-line);
    padding: 7px 8px calc(7px + env(safe-area-inset-bottom, 0px));
    -webkit-backdrop-filter: blur(14px);
    backdrop-filter: blur(14px);
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .msh[data-chrome-hidden="true"] .msh-bottom { transform: translateY(100%); }
  .msh-tab {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-height: 50px;
    border: 0;
    background: transparent;
    cursor: pointer;
    color: var(--msh-lo);
    border-radius: 12px;
    padding: 5px 0;
    transition: color 0.15s ease;
  }
  .msh-tab span {
    font-family: ${FM};
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 9.5px;
  }
  .msh-tab[data-active="true"] { color: var(--msh-hi); }
  .msh-tab[data-active="true"] svg { color: var(--msh-accent); }
  .msh-tab:active { background: var(--msh-surface); }

  /* ── Scrim + sheet ────────────────────────────────────────────────────────*/
  .msh-scrim {
    position: fixed;
    inset: 0;
    z-index: 48;
    border: 0;
    padding: 0;
    background: rgba(4, 7, 16, 0.55);
    -webkit-backdrop-filter: blur(2px);
    backdrop-filter: blur(2px);
    cursor: pointer;
    animation: msh-scrim-in 0.2s ease;
  }
  @keyframes msh-scrim-in { from { opacity: 0; } to { opacity: 1; } }
  .msh-sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 49;
    background: #0c1226;
    border-top-left-radius: 22px;
    border-top-right-radius: 22px;
    border-top: 1px solid var(--msh-line2);
    box-shadow: 0 -18px 50px -12px rgba(0, 0, 0, 0.6);
    transform: translateY(101%);
    transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
    max-height: 86vh;
    display: flex;
    flex-direction: column;
    padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
  }
  .msh-sheet.on { transform: translateY(0); }
  /* touch-action:none belongs ONLY on the drag surfaces, never on .msh-sheet:
     the effective touch behaviour intersects an element's touch-action with its
     ancestors', so none on the sheet also kills touch panning inside
     .msh-sheet-body — which overflows on any short phone (a 667px-tall viewport
     already clips the last section group), leaving those rows unreachable by
     swipe. The drag handler only starts from .msh-grab / .msh-sheet-h anyway. */
  .msh-grab,
  .msh-sheet-h { touch-action: none; }
  .msh-grab {
    width: 38px;
    height: 4px;
    border-radius: 3px;
    background: var(--msh-line2);
    margin: 9px auto 3px;
    flex: 0 0 auto;
  }
  .msh-sheet-h {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 16px 12px;
    border-bottom: 1px solid var(--msh-line);
    flex: 0 0 auto;
  }
  .msh-sheet-t {
    font-family: ${FM};
    text-transform: uppercase;
    letter-spacing: 0.2em;
    font-size: 11px;
    color: var(--msh-md);
  }
  .msh-back {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: 1px solid var(--msh-line);
    background: var(--msh-surface);
    color: var(--msh-md);
    cursor: pointer;
    flex: 0 0 auto;
  }
  .msh-sheet-body {
    overflow-y: auto;
    padding: 8px 12px 12px;
    -webkit-overflow-scrolling: touch;
  }

  /* Section-tree header doubles as the workspace switcher (tap → picker). */
  .msh-nav-h { padding: 6px 12px 10px; }
  .msh-wsbtn {
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    border: 0;
    background: transparent;
    color: var(--msh-hi);
    font: inherit;
    text-align: left;
    cursor: pointer;
    padding: 6px 10px 6px 8px;
    border-radius: 12px;
  }
  .msh-wsbtn:active { background: var(--msh-surface); }
  .msh-wsdot {
    width: 11px;
    height: 11px;
    border-radius: 3px;
    flex: 0 0 auto;
    background: var(--msh-accent);
    box-shadow: 0 0 11px -1px var(--msh-accent);
  }
  .msh-wsn {
    font-size: 20px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--msh-hi);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }
  .msh-wschev { flex: 0 0 auto; color: var(--msh-md); }

  /* Workspace rows (picker) */
  .msh-wrow {
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    text-align: left;
    border: 0;
    background: transparent;
    cursor: pointer;
    padding: 12px 10px;
    border-radius: 12px;
    color: var(--msh-hi);
  }
  .msh-wrow:active { background: var(--msh-surface); }
  .msh-wrow[data-active="true"] { background: var(--msh-surface); }
  .msh-wrow-n {
    flex: 0 0 auto;
    width: 34px;
    height: 34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 9px;
    background: color-mix(in srgb, var(--wac) 15%, transparent);
    border: 1px solid color-mix(in srgb, var(--wac) 42%, transparent);
    color: var(--wac);
    font-family: ${FM};
    font-size: 11px;
    letter-spacing: 0.02em;
  }
  .msh-wrow-b { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1 1 auto; }
  .msh-wrow-l { display: block; font-size: 15px; font-weight: 600; letter-spacing: -0.01em; color: var(--msh-hi); }
  .msh-wrow-d { display: block; font-size: 12px; line-height: 1.4; color: var(--msh-md); }

  /* Section rows (tree) */
  .msh-grp { margin-top: 2px; }
  .msh-grp-h {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 10px 6px;
    font-family: ${FM};
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-size: 10px;
    color: var(--msh-lo);
  }
  .msh-grp-n { color: var(--msh-lo); }
  .msh-srow {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    text-align: left;
    border: 0;
    background: transparent;
    cursor: pointer;
    padding: 12px 10px;
    border-radius: 11px;
    color: var(--msh-md);
  }
  .msh-srow:active { background: var(--msh-surface); }
  .msh-srow[data-active="true"] { background: var(--msh-navfill); color: var(--msh-hi); }
  .msh-sn {
    flex: 0 0 auto;
    width: 30px;
    font-family: ${FM};
    font-size: 11px;
    letter-spacing: 0.02em;
    color: var(--msh-lo);
  }
  .msh-srow[data-active="true"] .msh-sn { color: var(--msh-hi); }
  .msh-snm { flex: 1 1 auto; font-size: 15px; font-weight: 500; }

  /* Selected checkmark — only on the active row. */
  .msh-chk { flex: 0 0 auto; margin-left: auto; color: var(--msh-accent); opacity: 0; }
  .msh-srow[data-active="true"] .msh-chk,
  .msh-wrow[data-active="true"] .msh-chk { opacity: 1; }

  /* Account / more */
  .msh-acct {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 10px 10px 14px;
    border-bottom: 1px solid var(--msh-line);
    margin-bottom: 8px;
  }
  .msh-acct-n { display: block; font-size: 15px; font-weight: 600; color: var(--msh-hi); word-break: break-all; }
  .msh-acct-e {
    display: block;
    font-family: ${FM};
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-size: 9.5px;
    color: var(--msh-lo);
  }
  .msh-more-row { padding: 4px 10px 10px; }
  .msh-signout {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 13px 12px;
    border: 1px solid var(--msh-line);
    border-radius: 11px;
    background: transparent;
    color: var(--msh-md);
    font-family: ${FU};
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
  }
  .msh-signout:active { background: var(--msh-surface); }

  @media (prefers-reduced-motion: reduce) {
    .msh-top, .msh-bottom, .msh-sheet, .msh-scrim { transition: none; animation: none; }
  }
`;
